/**
 * CombatTurnPanel — top-level combat right-rail panel.
 *
 * Composed from all six rail sections (Task 8.6) in spec §2 order:
 *   YourTurn → ResonanceBudget → VitalPools → CombatantsList → ActiveState → RoundFlow
 *
 * Each section is collapsible via a ▾ header chevron. Collapse state is
 * held locally in a useState Record to persist across renders within the session.
 *
 * Phase 7 (scaffold) + Phase 8 (rail sections) of the unified-combat-ui plan.
 * See: docs/superpowers/specs/2026-05-23-unified-combat-ui-design.md §2
 */

import { CompulsionMark } from '@/combat/components/CompulsionBanner';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { AudereOfferGate } from '@/magic/components/AudereOfferGate';
import { AudereMajoraOfferGate } from '@/magic/components/AudereMajoraOfferGate';
import { UltimateRevealGate } from '@/magic/components/UltimateRevealGate';
import {
  useAvailableActions,
  useCombatEncounter,
  useConsequenceOutcomes,
  useJoinMutation,
  useLeaveMutation,
} from './queries';
import { DuelYieldControls, DuelAcknowledgeRiskBanner } from './duels/DuelChallengeControls';
import { YourTurn } from './sections/YourTurn';
import { PendingAttacks } from './components/PendingAttacks';
import { ResonanceBudget } from './sections/ResonanceBudget';
import { VitalPools, findOwnParticipant } from './sections/VitalPools';
import { Pressure } from './sections/Pressure';
import { CombatantsList } from './sections/CombatantsList';
import { CompanionOrders } from './sections/CompanionOrders';
import { ActiveState } from './sections/ActiveState';
import { RoundFlow } from './sections/RoundFlow';
import { EncounterOutcomeBanner } from './components/EncounterOutcomeBanner';
import type { AftermathDigest } from './components/AftermathDigest';
import { ForcedEscapeBanner } from './components/ForcedEscapeBanner';
import { SpecialistChoicePanel } from './components/SpecialistChoicePanel';
import { CommitmentPanel } from './components/CommitmentPanel';
import { OutcomeRoulette } from './OutcomeRoulette';
import { RollBreakdownCard } from './components/RollBreakdownCard';
import { StandoffCard } from './standoff/StandoffCard';
import { StandoffSummaryCard } from './standoff/StandoffSummaryCard';
import type { EncounterDetail } from './types';
import type { CastPosition, PositionTargetShape } from '@/actions/types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CombatTurnPanelProps {
  encounterId: number;
  characterId: number;
  characterSheetId: number;
  /**
   * Cast-time position selection, lifted to CombatRail (#2206) so this
   * panel's YourTurn section and the sibling tactical-map tab share it.
   * Optional — forwarded to YourTurn, which falls back to local state when
   * the caller (e.g. tests rendering CombatTurnPanel standalone) omits it.
   */
  castPosition?: CastPosition;
  onCastPositionChange?: (next: CastPosition) => void;
  /** Forwarded to YourTurn — reports the selected technique's position shape. */
  onPositionShapeChange?: (shape: PositionTargetShape) => void;
  /** Forwarded to the outcome banner's Dismiss button once the encounter is completed (#3551). */
  onDismissOutcome?: () => void;
}

// ---------------------------------------------------------------------------
// Section names (for collapse state keying)
// ---------------------------------------------------------------------------

type SectionName =
  | 'yourTurn'
  | 'resonanceBudget'
  | 'vitalPools'
  | 'pressure'
  | 'combatantsList'
  | 'companionOrders'
  | 'activeState'
  | 'roundFlow'
  | 'outcomeRoulette';

const DEFAULT_COLLAPSE_STATE: Record<SectionName, boolean> = {
  yourTurn: false,
  resonanceBudget: false,
  vitalPools: false,
  pressure: false,
  combatantsList: false,
  companionOrders: false,
  activeState: false,
  roundFlow: false,
  outcomeRoulette: false,
};

// ---------------------------------------------------------------------------
// CombatTurnPanel
// ---------------------------------------------------------------------------

/** Enough rows for one round's rolls by one character (#4884). */
const LAST_ROUND_PAGE_SIZE = 20;

type ConsequenceOutcomes = NonNullable<ReturnType<typeof useConsequenceOutcomes>['data']>;

/** Keep the most recent round (or the latest interaction for older records), oldest first. */
function lastRoundOutcomes(outcomes: ConsequenceOutcomes | undefined): ConsequenceOutcomes {
  const latest = outcomes?.[0];
  if (!latest) return [];
  return (outcomes ?? [])
    .filter((row) =>
      latest.combat_round != null
        ? row.combat_round === latest.combat_round
        : row === latest ||
          (latest.combat_interaction_id != null &&
            row.combat_interaction_id === latest.combat_interaction_id)
    )
    .reverse();
}

function AudereActiveStrip({ active, state }: { active: boolean; state: string | null }) {
  if (!active) return null;
  return (
    <div
      className="rounded-md border border-fuchsia-500/60 bg-fuchsia-500/10 px-3 py-1.5 text-center text-xs font-bold uppercase tracking-[0.3em] text-fuchsia-800 dark:bg-fuchsia-950/40 dark:text-fuchsia-300"
      data-testid="audere-active-strip"
    >
      {state === 'audere_majora' ? 'Audere Majora' : 'Audere'}
    </div>
  );
}

function StandoffCards({
  encounter,
  encounterId,
  characterId,
}: {
  encounter: EncounterDetail;
  encounterId: number;
  characterId: number;
}) {
  return (
    <>
      {encounter.standoff ? (
        <StandoffCard
          standoff={encounter.standoff}
          encounterId={encounterId}
          characterId={characterId}
        />
      ) : null}
      {encounter.standoff_summary ? (
        <StandoffSummaryCard summary={encounter.standoff_summary} />
      ) : null}
    </>
  );
}

export function CombatTurnPanel({
  encounterId,
  characterId,
  characterSheetId,
  castPosition,
  onCastPositionChange,
  onPositionShapeChange,
  onDismissOutcome,
}: CombatTurnPanelProps) {
  // Encounter state
  const {
    data: encounter,
    isLoading: encounterLoading,
    isError: encounterError,
  } = useCombatEncounter(encounterId);

  // Available COMBAT-backend actions for the character (includes clash refs) and its moves.
  const { data: combatActions } = useAvailableActions(characterId);

  // Every roll this character made in the most recent round of the current encounter
  // (#4884): a Strike's hit and damage rolls, the defense roll against each NPC blow, and
  // any knockout or wound rolls those blows caused. A page of 20 covers a round; rows with
  // no round (older records) fall back to the rolls sharing the newest row's interaction.
  // Shown oldest first.
  const { data: consequenceOutcomes } = useConsequenceOutcomes({
    character: characterId,
    encounter: encounterId,
    page_size: LAST_ROUND_PAGE_SIZE,
  });
  const latestOutcome = consequenceOutcomes?.[0] ?? null;
  const latestOutcomes = lastRoundOutcomes(consequenceOutcomes);

  // Collapse state — all sections start expanded.
  const [collapsed, setCollapsed] = useState<Record<SectionName, boolean>>(DEFAULT_COLLAPSE_STATE);

  // Open Encounter join/leave mutations — must be called unconditionally (rules of hooks).
  const { mutate: joinEncounter, isPending: isJoining } = useJoinMutation(encounterId);
  const { mutate: leaveEncounter, isPending: isLeaving } = useLeaveMutation(encounterId);

  function toggleSection(section: SectionName) {
    setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (encounterLoading) {
    return (
      <div className="p-4 text-sm text-muted-foreground" data-testid="combat-panel-loading">
        Loading combat state…
      </div>
    );
  }

  if (encounterError || encounter === undefined) {
    return (
      <div className="p-4 text-sm text-destructive" data-testid="combat-panel-error">
        Failed to load encounter.
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Completed encounter — outcome banner replaces the live rail sections (#876)
  // ---------------------------------------------------------------------------

  if (encounter.status === 'completed') {
    return (
      <CompletedEncounter
        encounter={encounter}
        characterId={characterId}
        onDismissOutcome={onDismissOutcome}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const isParticipant = encounter.is_participant;
  const roundNumber = encounter.round_number ?? 0;

  const isOpenEncounter = encounter.encounter_type === 'open_encounter';
  const isBetweenRounds = encounter.status === 'between_rounds';

  // Duel-specific flags — derived from encounter fields added in Task 13 (#568).
  // Note: the `encounter.status === 'completed'` case is handled by the early return
  // above, so after this point the encounter is guaranteed non-completed.
  const isDuel = encounter.encounter_type === 'duel';
  const isActiveDuel = isDuel && isParticipant;
  // is_lethal is a readonly field on EncounterDetail (Task 13). Show the risk
  // acknowledgement banner when the duel is lethal; it hides itself locally after
  // the acknowledge_risk action dispatches successfully.
  const showAcknowledgeRisk = isActiveDuel && encounter.is_lethal;

  // Audere active strip — the viewer's own participant row (matched by
  // character_sheet_id) carries audere_state while the breakthrough is live. The
  // server finds the conditions by their designation on the Audere threshold, so
  // staff can rename them freely (#4739).
  const viewerParticipant = findOwnParticipant(encounter.participants, characterSheetId);
  const audereState = viewerParticipant?.audere_state ?? null;
  const isCeremonyActive = audereState !== null;

  return (
    <div
      className={cn('flex flex-col gap-3 rounded-lg border border-border bg-card p-3 shadow-sm')}
      data-testid="combat-turn-panel"
    >
      {/* Panel header — round number + observer badge */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-foreground">
          {encounter.standoff || encounter.standoff_summary
            ? 'Standoff'
            : `Your Turn: Round ${roundNumber}`}
        </h2>
        {!isParticipant && (
          <span className="rounded border border-border bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            Observer
          </span>
        )}
      </div>

      {encounter.objective && (
        <div
          className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
          data-testid="combat-objective"
        >
          <p className="font-semibold text-foreground">Objective</p>
          <p className="text-muted-foreground">
            {encounter.objective.label || encounter.objective.key}
          </p>
          {encounter.objective.clock && (
            <p className="text-muted-foreground">
              Time pressure: {String(encounter.objective.clock.filled ?? 0)}/
              {String(encounter.objective.clock.size ?? 0)}
            </p>
          )}
        </div>
      )}

      <SpecialistChoicePanel
        encounterId={encounterId}
        selections={encounter.pending_selections ?? []}
      />
      <CommitmentPanel encounter={encounter} />

      {/* Forced-escape banner — Hero Killer on field; victory impossible (#875). */}
      {encounter.forced_escape && <ForcedEscapeBanner />}

      {/* 0. Audere offer gate — the most dramatic prompt in combat (#873).
          Renders null unless a pending offer exists for this character. */}
      <AudereOfferGate
        characterSheetId={characterSheetId}
        characterId={characterId}
        encounterId={encounterId}
      />
      {/* 0b. Audere Majora crossing offer gate (#543).
          Renders null unless a pending crossing offer exists for this character. */}
      <AudereMajoraOfferGate
        characterSheetId={characterSheetId}
        characterId={characterId}
        encounterId={encounterId}
      />

      {/* 0c. Ultimate reveal + deferred-death banner (#4098). */}
      <UltimateRevealGate
        characterSheetId={characterSheetId}
        characterId={characterId}
        encounterId={encounterId}
        isCeremonyActive={isCeremonyActive}
      />

      {/* Active-Audere strip: visible while either Audere ceremony runs. */}
      <AudereActiveStrip active={isCeremonyActive} state={audereState} />

      {/* Duel: lethal-risk acknowledgement banner — shown above all other duel controls
          so the participant cannot miss it. Hides itself after dispatching acknowledge_risk.
          Backend gap: acknowledgement state is not yet surfaced on EncounterDetail;
          we show the banner whenever is_lethal is true and let the server gate actions. */}
      <DuelAcknowledgeRiskBanner characterId={characterId} showBanner={showAcknowledgeRisk} />

      {/* Duel: yield button — shown for active-duel participants; hides after yielding. */}
      <DuelYieldControls characterId={characterId} isActiveDuel={isActiveDuel} />

      {/* Open Encounter join/leave — only between rounds */}
      {isOpenEncounter && isBetweenRounds && !isParticipant && (
        <button
          type="button"
          data-testid="open-encounter-join-btn"
          onClick={() => joinEncounter(characterSheetId)}
          disabled={isJoining}
          className="w-full rounded-md border border-border bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isJoining ? 'Joining…' : 'Join Encounter'}
        </button>
      )}
      {/* Standoff (#4145): the pre-round card, present only before round 1. */}
      <StandoffCards encounter={encounter} encounterId={encounterId} characterId={characterId} />

      {/* A standoff is pre-round: its card is the whole panel until round one begins. */}
      {encounter.standoff || encounter.standoff_summary ? null : (
        <>
          {/* §2 — Section order: YourTurn → ResonanceBudget → VitalPools →
            CombatantsList → ActiveState → RoundFlow */}

          {/* 1. YourTurn — only for participants */}
          {isParticipant ? (
            <>
              <YourTurn
                encounterId={encounterId}
                characterId={characterId}
                characterSheetId={characterSheetId}
                roundNumber={roundNumber}
                availableActions={combatActions}
                readOnly={false}
                encounter={encounter}
                castPosition={castPosition}
                onCastPositionChange={onCastPositionChange}
                onPositionShapeChange={onPositionShapeChange}
              />
              {/* The compulsion banner (in YourTurn) sits above Leave (#4802). */}
              {isOpenEncounter && isBetweenRounds && isParticipant && (
                <CompulsionMark compulsion={viewerParticipant?.compulsion} actionKey="leave">
                  <button
                    type="button"
                    data-testid="open-encounter-leave-btn"
                    onClick={() => leaveEncounter()}
                    disabled={isLeaving}
                    className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground hover:bg-muted/80 disabled:opacity-50"
                  >
                    {isLeaving ? 'Leaving…' : 'Leave Encounter'}
                  </button>
                </CompulsionMark>
              )}
            </>
          ) : (
            <>
              <PendingAttacks
                attacks={encounter.pending_attacks ?? []}
                viewerParticipantId={null}
              />
              <p className="text-xs text-muted-foreground">You are observing this encounter.</p>
            </>
          )}

          {/* 2. ResonanceBudget */}
          <ResonanceBudget
            characterSheetId={characterSheetId}
            collapsed={collapsed.resonanceBudget}
            onToggleCollapse={() => toggleSection('resonanceBudget')}
            data-testid="section-resonance-budget"
          />

          {/* 3. VitalPools */}
          <VitalPools
            encounter={encounter}
            characterId={characterId}
            characterSheetId={characterSheetId}
            collapsed={collapsed.vitalPools}
            onToggleCollapse={() => toggleSection('vitalPools')}
            data-testid="section-vital-pools"
          />

          {/* 3b. Pressure: intensity, control, and control surrendered to Fury (#4738) */}
          <Pressure
            encounter={encounter}
            characterSheetId={characterSheetId}
            collapsed={collapsed.pressure}
            onToggleCollapse={() => toggleSection('pressure')}
          />

          {/* 4. CombatantsList — opponent click-menu (#3381) only for a declaring-phase
            participant; canDeclareManeuvers mirrors YourTurn's own isDeclaringPhase
            gate, threaded down from this shared parent so a GM/observer row never
            renders the menu. */}
          <CombatantsList
            encounter={encounter}
            collapsed={collapsed.combatantsList}
            onToggleCollapse={() => toggleSection('combatantsList')}
            characterId={characterId}
            canDeclareManeuvers={isParticipant && encounter.status === 'declaring'}
            compulsion={viewerParticipant?.compulsion ?? null}
            data-testid="section-combatants-list"
          />

          {/* 5. CompanionOrders — free directives for deployed companions (#3576). */}
          <CompanionOrders
            encounter={encounter}
            encounterId={encounterId}
            characterId={characterId}
            collapsed={collapsed.companionOrders}
            onToggleCollapse={() => toggleSection('companionOrders')}
          />

          {/* 6. ActiveState */}
          <ActiveState
            encounter={encounter}
            collapsed={collapsed.activeState}
            onToggleCollapse={() => toggleSection('activeState')}
            data-testid="section-active-state"
          />

          {/* 7. RoundFlow */}
          <RoundFlow
            encounter={encounter}
            collapsed={collapsed.roundFlow}
            onToggleCollapse={() => toggleSection('roundFlow')}
            data-testid="section-round-flow"
          />
        </>
      )}

      {/* 7. OutcomeRoulette — most recent consequence outcome for this character */}
      {latestOutcome !== null && (
        <div
          className="rounded-md border border-border bg-card"
          data-testid="outcome-roulette-section"
        >
          <button
            type="button"
            onClick={() => toggleSection('outcomeRoulette')}
            className="flex w-full items-center justify-between px-3 py-2 text-left"
            aria-expanded={!collapsed.outcomeRoulette}
            data-testid="outcome-roulette-toggle"
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Last Outcome
            </span>
            <span
              className={cn(
                'text-muted-foreground transition-transform',
                collapsed.outcomeRoulette ? '-rotate-90' : 'rotate-0'
              )}
              aria-hidden="true"
            >
              ▾
            </span>
          </button>
          {!collapsed.outcomeRoulette && (
            <div className="space-y-3 border-t border-border px-3 py-2">
              {latestOutcomes.map((row) =>
                row.roll_breakdown ? (
                  <RollBreakdownCard key={row.id} breakdown={row.roll_breakdown} />
                ) : (
                  <OutcomeRoulette
                    key={row.id}
                    // A roll with no pool and no challenge has no roulette: never show
                    // the server's placeholder row as if it were a result.
                    outcomeDisplay={
                      !row.pool && !row.challenge_record_id ? [] : row.outcome_display
                    }
                    modifiers={row.modifiers}
                    modifierTotal={row.modifier_total}
                    summary={row.summary}
                  />
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompletedEncounter({
  encounter,
  characterId,
  onDismissOutcome,
}: {
  encounter: EncounterDetail;
  characterId: number;
  onDismissOutcome?: () => void;
}) {
  // Pre-#876 rows completed without a recorded outcome send a blank string.
  const outcome = encounter.outcome || 'abandoned';
  // The server only sends aftermath data to its owner or a GM.
  const digests = encounter.participants
    .filter((participant) => participant.aftermath != null)
    .map((participant) => ({
      participantId: participant.id,
      characterName: participant.character_name,
      digest: participant.aftermath as AftermathDigest,
    }));
  return (
    <div
      className={cn('flex flex-col gap-3 rounded-lg border border-border bg-card p-3 shadow-sm')}
      data-testid="combat-turn-panel"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-foreground">
          Encounter Concluded: Round {encounter.round_number ?? 0}
        </h2>
      </div>
      <EncounterOutcomeBanner
        outcome={outcome}
        digests={digests}
        onDismiss={onDismissOutcome}
        characterId={characterId}
        // No scene id, no Settle: WonOverRows hides it rather than post to ''.
        sceneId={encounter.scene ? String(encounter.scene) : null}
      />
    </div>
  );
}
